import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getTextField } from '@lender/utils';
import { Box, Button, ClickAwayListener, Paper, Popper, Typography, useTheme } from '@mui/material';
import { hudApi } from 'app/services/api';
import {
  AutocompleteCDS,
  onAutocompleteChangeInForm,
  RadioGroupCDS,
  TextFieldCDS,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import { useAddressAutocomplete } from 'app/shared/services/addressAutocomplete';
import {
  cityWrapperStyles,
  getDropdownItemStyles,
  wrapperStyles,
} from 'app/shared/services/addressAutocomplete/addressSuggestionDropdown.styles';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { object } from 'yup';

import { stateOptionsAbbreviated } from 'constants/options';
import useForm from 'hooks/useForm';
import { logger } from 'utils/logger';
import { getZipField } from 'utils/validation';

import { closeDialogAction, showMessageAction } from 'store/global/actions';

import { BorrowersTestIds } from '../../../../../../../Borrowers.testIds';
import { DeclarationType, defaultRadioInputProps } from '../../Declarations.constants';
import { counselingFormStyles } from '../../Declarations.styles';
import { getModelPath } from '../HomeownershipEducationAndHousingCounseling.config';
import {
  agencyPlacesWidgetMap,
  CounselingAgencyType,
  searchByOptions,
} from '../HomeownershipEducationAndHousingCounseling.constants';

import AgencyTable from './AgencyTable';

const cityStateSchema = object().shape({
  city: getTextField(),
  state: getTextField(),
});

const zipSchema = object().shape({
  zip: getZipField({ isRequired: true }),
});

const initialFormValues = { searchFormat: true, selectedAgencyId: '', agenciesData: [] };

const DeclarationsCounselingForm = ({
  isLoading,
  setIsLoading,
  setInParentForm,
  validateField: validateParentField,
  entityId,
  agencyType,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [isSearchInitiated, setIsSearchInitiated] = useState(false);

  const formRef = useRef();
  const cityInputRef = useRef(null);
  const zipInputRef = useRef(null);

  const dropdownItemStyle = useMemo(() => getDropdownItemStyles(theme), [theme]);

  const {
    suggestions: citySuggestions,
    search: searchCity,
    clearSuggestions: clearCitySuggestions,
  } = useAddressAutocomplete({ searchTypes: null });

  const {
    suggestions: zipSuggestions,
    search: searchZip,
    clearSuggestions: clearZipSuggestions,
  } = useAddressAutocomplete({ searchTypes: null });

  const {
    form,
    isFormChanged,
    setInForm,
    setMultipleInForm,
    validationErrors,
    setSchema,
    handleChange,
    validateForm,
    validateField,
    setValidationErrors,
  } = useForm(initialFormValues);

  const isSearchByZip = !form.searchFormat;

  useEffect(() => {
    setSchema(isSearchByZip ? zipSchema : cityStateSchema);
  }, [isSearchByZip, setSchema]);

  const onSearchAgencies = useCallback(async () => {
    if (!(await validateForm({ noFormChangedValidate: true }))) {
      return;
    }

    setIsLoading(true);

    const requestData = isSearchByZip
      ? { zip: form.zip }
      : {
          city: form.city,
          state: form.state?.value,
        };

    hudApi
      .getAgencies(requestData)
      .then(({ data }) => {
        const formattedData = data?.map(agency => ({
          ...agency,
          IdNumber: agency.IDNumber,
        }));

        setInForm('agenciesData', _.sortBy(formattedData, 'name'), false);
        setIsSearchInitiated(true);
      })
      .catch(err => {
        dispatch(showMessageAction({ message: err.toString(), variant: 'error' }));
        logger.error(err);
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, form, isSearchByZip, setInForm, setIsLoading, validateForm]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      const selectedAgency = form.agenciesData.find(({ id }) => id === form.selectedAgencyId);

      const dataToSetInParentForm = agencyPlacesWidgetMap.reduce((acc, { fieldName, value }) => {
        acc[`${agencyType}${fieldName}`] = selectedAgency[value];

        return acc;
      }, {});

      const customAction = sectionForm => {
        if (!sectionForm) {
          return;
        }

        const fieldNameToValidate =
          agencyType === CounselingAgencyType.EDUCATION_COUNSELING_AGENCY
            ? 'homeownershipEducationNameOfTheHousingEducationProgram'
            : 'housingCounselingNameOfTheHousingEducationProgram';

        setTimeout(() =>
          validateParentField(
            `${getModelPath(entityId)}${fieldNameToValidate}`,
            sectionForm[fieldNameToValidate],
          ),
        );
      };

      setInParentForm(
        DeclarationType.HOMEOWNERSHIP_EDUCATION_AND_HOUSING_COUNSELING,
        dataToSetInParentForm,
        customAction,
      );

      dispatch(closeDialogAction());
    },
    [
      agencyType,
      dispatch,
      form.agenciesData,
      form.selectedAgencyId,
      setInParentForm,
      validateForm,
      entityId,
      validateParentField,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onReapiCitySelect = useCallback(
    suggestion => {
      const { mapped } = suggestion;
      const stateAbbreviated = stateOptionsAbbreviated.find(
        ({ value }) => value === mapped.state?.value,
      );

      setMultipleInForm({ city: mapped.city, state: stateAbbreviated });
      setValidationErrors({});
      clearCitySuggestions();
    },
    [setMultipleInForm, setValidationErrors, clearCitySuggestions],
  );

  const onReapiZipSelect = useCallback(
    suggestion => {
      setInForm('zip', suggestion.mapped.zip || null);
      setValidationErrors({});
      clearZipSuggestions();
    },
    [setInForm, setValidationErrors, clearZipSuggestions],
  );

  const onCityInputChange = useCallback(
    e => {
      searchCity(e.target.value);
      handleChange(e);
    },
    [searchCity, handleChange],
  );

  const onZipInputChange = useCallback(
    e => {
      searchZip(e.target.value);
      handleChange(e);
    },
    [searchZip, handleChange],
  );

  const onChangeFormat = useCallback(
    e => {
      setValidationErrors({});
      clearCitySuggestions();
      clearZipSuggestions();
      handleChange(e);
    },
    [handleChange, setValidationErrors, clearCitySuggestions, clearZipSuggestions],
  );

  const onItemMouseDown = useCallback(e => e.preventDefault(), []);

  const onZipItemClick = useCallback(
    e => {
      const index = Number(e.currentTarget.dataset.itemId);
      const suggestion = zipSuggestions[index];

      if (suggestion) {
        onReapiZipSelect(suggestion);
      }
    },
    [zipSuggestions, onReapiZipSelect],
  );

  const onCityItemClick = useCallback(
    e => {
      const index = Number(e.currentTarget.dataset.itemId);
      const suggestion = citySuggestions[index];

      if (suggestion) {
        onReapiCitySelect(suggestion);
      }
    },
    [citySuggestions, onReapiCitySelect],
  );

  const zipPopperWidth = zipInputRef.current?.offsetWidth;
  const zipPopperStyle = zipSuggestions.length ? { zIndex: 1300, width: zipPopperWidth } : null;

  const cityPopperWidth = cityInputRef.current?.offsetWidth;
  const cityPopperStyle = citySuggestions.length ? { zIndex: 1300, width: cityPopperWidth } : null;

  return (
    <form
      noValidate
      className="w-full"
      data-is-zip={isSearchByZip}
      data-ischanged={isFormChanged}
      data-testid={BorrowersTestIds.DECLARATIONS_COUNSELING_FORM}
      ref={formRef}
      onSubmit={onSubmit}
    >
      <Box sx={counselingFormStyles}>
        <div>
          <RadioGroupCDS
            {...defaultRadioInputProps}
            dataTestId={BorrowersTestIds.DECLARATIONS_COUNSELING_FORM_SEARCH_BY_RADIO}
            disabled={isLoading}
            label="Search by:"
            name="searchFormat"
            options={searchByOptions}
            value={form.searchFormat}
            onChange={onChangeFormat}
          />
        </div>

        {isSearchByZip ? (
          <ClickAwayListener onClickAway={clearZipSuggestions}>
            <div style={wrapperStyles}>
              <TextFieldCDS
                data-testid={BorrowersTestIds.DECLARATIONS_COUNSELING_FORM_ZIP_INPUT}
                disabled={isLoading || form.searchFormat}
                error={!!validationErrors.zip}
                helperText={validationErrors.zip}
                inputRef={zipInputRef}
                label="Zip Code"
                maxLength={5}
                name="zip"
                placeholder="-"
                type="text"
                value={form.zip}
                variant="outlined"
                width="360px"
                onChange={onZipInputChange}
              />
              {!!zipSuggestions.length && (
                <Popper
                  open
                  anchorEl={zipInputRef.current}
                  placement="bottom-start"
                  style={zipPopperStyle}
                >
                  <Paper elevation={3} role="listbox">
                    {zipSuggestions.map((suggestion, idx) => {
                      const itemKey = `${suggestion.raw.postcode}-${suggestion.raw.city}-${suggestion.raw.state}-${idx}`;

                      return (
                        <div
                          data-item-id={idx}
                          key={itemKey}
                          role="option"
                          style={dropdownItemStyle}
                          onClick={onZipItemClick}
                          onMouseDown={onItemMouseDown}
                        >
                          {suggestion.raw.postcode}
                          {suggestion.raw.city ? ` - ${suggestion.raw.city}` : ''}
                          {suggestion.raw.state ? `, ${suggestion.raw.state}` : ''}
                        </div>
                      );
                    })}
                  </Paper>
                </Popper>
              )}
            </div>
          </ClickAwayListener>
        ) : (
          <div className="flex gap-16">
            <ClickAwayListener onClickAway={clearCitySuggestions}>
              <div style={cityWrapperStyles}>
                <TextFieldCDS
                  fullWidth
                  required
                  data-testid={BorrowersTestIds.DECLARATIONS_COUNSELING_FORM_CITY_INPUT}
                  disabled={isLoading || !form.searchFormat}
                  error={!!validationErrors.city}
                  helperText={validationErrors.city}
                  inputRef={cityInputRef}
                  label="City"
                  name="city"
                  placeholder="-"
                  value={form.city}
                  variant="outlined"
                  width="360px"
                  onChange={onCityInputChange}
                />
                {!!citySuggestions.length && (
                  <Popper
                    open
                    anchorEl={cityInputRef.current}
                    placement="bottom-start"
                    style={cityPopperStyle}
                  >
                    <Paper elevation={3} role="listbox">
                      {citySuggestions.map((suggestion, idx) => {
                        const itemKey = `${suggestion.raw.city}-${suggestion.raw.state}-${suggestion.raw.postcode}-${idx}`;

                        return (
                          <div
                            data-item-id={idx}
                            key={itemKey}
                            role="option"
                            style={dropdownItemStyle}
                            onClick={onCityItemClick}
                            onMouseDown={onItemMouseDown}
                          >
                            {[suggestion.raw.city, suggestion.raw.state, suggestion.raw.postcode]
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                        );
                      })}
                    </Paper>
                  </Popper>
                )}
              </div>
            </ClickAwayListener>

            <AutocompleteCDS
              fullWidth
              required
              dataTestIdInput={
                BorrowersTestIds.DECLARATIONS_COUNSELING_FORM_STATE_AUTOCOMPLETE_INPUT
              }
              dataTestIdOption={BorrowersTestIds.DECLARATIONS_COUNSELING_FORM_STATE_OPTION}
              disabled={isLoading || !form.searchFormat}
              error={!!validationErrors.state}
              helperText={validationErrors.state}
              label="State"
              name="state"
              options={stateOptionsAbbreviated}
              value={form.state}
              variant="outlined"
              width="360px"
              onChange={onAutocompleteChange}
            />
          </div>
        )}

        <div className="flex justify-start mt-8">
          <Button
            data-testid={BorrowersTestIds.DECLARATIONS_COUNSELING_FORM_SEARCH_BUTTON}
            disabled={isLoading}
            onClick={onSearchAgencies}
          >
            Search
          </Button>
        </div>
        {form.agenciesData.length ? (
          <AgencyTable
            agenciesData={form.agenciesData}
            selectedAgencyId={form.selectedAgencyId}
            setInForm={setInForm}
          />
        ) : (
          isSearchInitiated && (
            <Typography className="mt-8" variant="regular14">
              There is no data to display
            </Typography>
          )
        )}
        <div className="flex justify-end">
          <Button
            data-testid={BorrowersTestIds.DECLARATIONS_COUNSELING_FORM_SUBMIT_BUTTON}
            disabled={isLoading || !form.selectedAgencyId}
            type="submit"
          >
            Done
          </Button>
        </div>
      </Box>
    </form>
  );
};

DeclarationsCounselingForm.propTypes = {
  ...WithLoadingPropTypes,
  setInParentForm: PropTypes.func.isRequired,
  agencyType: PropTypes.string.isRequired,
};

export default compose(withLoading, memo)(DeclarationsCounselingForm);
